import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { userApi } from '../../api/user';
import { User, UserFilter, UserRole } from '../../types/user';

const PAGE_SIZE = 10;

const roleLabels: Record<UserRole, string> = {
  ADMIN: '管理者',
  USER: '一般ユーザー',
};

const roleColors: Record<UserRole, 'primary' | 'default'> = {
  ADMIN: 'primary',
  USER: 'default',
};

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState<UserFilter>({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', filter],
    queryFn: () => userApi.getUsers(filter),
  });

  const deleteMutation = useMutation({
    mutationFn: userApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialogOpen(false);
    },
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setFilter((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter((prev) => ({
      ...prev,
      role: event.target.value as UserRole | undefined,
      page: 1,
    }));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter((prev) => ({
      ...prev,
      search: event.target.value,
      page: 1,
    }));
    setPage(0);
  };

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (id: string) => {
    navigate(`/users/${id}/edit`);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedUserId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUserId) {
      try {
        await deleteMutation.mutateAsync(selectedUserId);
      } catch (error) {
        console.error('ユーザーの削除に失敗しました:', error);
      }
    }
    setDeleteDialogOpen(false);
    setSelectedUserId(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">データの読み込みに失敗しました</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">ユーザー管理</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          新規作成
        </Button>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="ロール"
            value={filter.role || ''}
            onChange={handleRoleChange}
          >
            <MenuItem value="">すべて</MenuItem>
            <MenuItem value="ADMIN">管理者</MenuItem>
            <MenuItem value="USER">一般ユーザー</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <TextField
            fullWidth
            label="検索"
            value={filter.search || ''}
            onChange={handleSearchChange}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ロール</TableCell>
              <TableCell>作成日時</TableCell>
              <TableCell>更新日時</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username || user.email}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={roleLabels[user.role]}
                    color={roleColors[user.role]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(user.createdAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                </TableCell>
                <TableCell>
                  {format(new Date(user.updatedAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditUser(user.id)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(user.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ユーザーの削除</DialogTitle>
        <DialogContent>
          <Typography>
            このユーザーを削除してもよろしいですか？この操作は取り消すことができません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListPage; 